import Vue from 'vue';
import uniqid from 'uniqid';

import { get, cloneDeep, round, removeKey, orderBy, pick } from '@/helpers';
import { getShippingFee } from '@/extensions/shop/helpers';

import ShopApi from '@/extensions/shop/store/api/shop';
// import apolloClient from '@/store/apollo';

// initial state
const state = {
    preferredListOrientation: localStorage.getItem('product-list-orientation'), // 'horizontal' | 'vertical' | 'justified'
    cart: {
        initialised: false,
        articlePivots: [],
        qty: 0,
        tax: 0,
        sum: 0
    },
    checkout: {
        position: 1,
        initialised: false,
        initialising: false,
        wait: false, // prevent submitting while transition animation leads to valid
        saving: false,
        success: false,
        steps: [
            {
                key: 'cart',
                position: 1,
                label: 'Warenkorb',
                icon: 'shopping-cart'
            },
            {
                key: 'account',
                position: 2,
                label: 'Benutzerdaten',
                icon: 'user'
            },
            {
                key: 'shipping',
                position: 3,
                label: 'Versand',
                icon: 'dolly-flatbed'
            },
            {
                key: 'payment',
                position: 4,
                label: 'Bestellen',
                icon: 'money-bill'
            }
        ],
        form: {
            account: {
                // will get a purchaser
                id: undefined, // member id: undefined -> guest
                create: false,
                gender: 'M',
                first_name: '',
                last_name: '',
                mobile: '',
                phone: '',
                email: '',
                password: '',
                billing_address: {
                    // default_billing address data from member
                    id: undefined,
                    gender: 'M',
                    first_name: '',
                    last_name: '',
                    company: '',
                    street: '',
                    street_addition: '',
                    zipcode: '',
                    city: '',
                    country: '', // ISO-2 code
                    type: 'billing',
                    latitude: undefined,
                    longitude: undefined
                },
                shipping_address: {
                    id: undefined,
                    gender: 'M',
                    first_name: '',
                    last_name: '',
                    company: '',
                    street: '',
                    street_addition: '',
                    zipcode: '',
                    city: '',
                    country: '', // ISO-2 code
                    type: 'billing',
                    latitude: undefined,
                    longitude: undefined
                }
            },
            coupons: [],
            payment: {
                id: undefined // payment method
            },
            shipping: {
                id: undefined // shipping method
            }
        }
    },
    product: undefined,
    customer: undefined // TODO: null
};

// getters
const getters = {
    id: (state, getters, rootState, rootGetters) => get(rootGetters, 'CMS.shop.id'),
    cart: state => state.cart,
    cartSummary: (state, getters) => {
        let summary = [];
        let showNetDetails = false;

        // check if there are net details that need to be shown
        // i.e. discount has been applied as net fixed and/or shipping fee had no tax
        // also respect customer->tax_free and customer->show_net_prices
        if (
            (state.customer && state.customer.show_net_prices && !state.customer.tax_free) ||
            (getters.shippingFee && getters.shippingFee === getters.shippingFeeNet)
        ) {
            showNetDetails = true;
        }

        if (showNetDetails) {
            // show net details

            let net = 0;
            let tax = 0;

            // sub_total_net
            if (getters.subTotalNet !== getters.grandTotal) {
                net += getters.subTotalNet;
                tax += getters.subTotal - getters.subTotalNet;

                let label = 'Netto';
                if (state.customer && state.customer.tax_free) label = 'Zwischensumme';

                summary.push({
                    key: 'sub-total-net',
                    label,
                    amount: getters.subTotalNet
                    // formatted: getters.formattedSubTotalNet,
                });
            }

            // shippingFeeNet
            if (getters.shippingFee && getters.shippingFee === getters.shippingFeeNet) {
                net += getters.shippingFeeNet;
                tax += getters.shippingFee - getters.shippingFeeNet;

                summary.push({
                    key: 'shipping-fee-net',
                    label: 'Versandkosten',
                    amount: getters.shippingFeeNet
                    // formatted: getters.formattedShippingFeeNet
                });
            }

            // show tax and sub_total (gross) if necessary

            if (tax && getters.taxTotal !== undefined && round(tax, 2) !== round(getters.taxTotal, 2)) {
                summary.push({
                    key: 'tax-sum',
                    label: 'MwSt.',
                    amount: tax
                    // formatted => getFormattedMoneyValue((int) getDatabaseFriendlyMoneyValue($tax), $this->currency ?: 'EUR', 'de_DE'),
                });
            }

            if (net + tax !== getters.grandTotal) {
                summary.push({
                    key: 'sub-total',
                    label: 'Zwischensumme',
                    amount: net + tax
                    // formatted: getFormattedMoneyValue((int) getDatabaseFriendlyMoneyValue($net + $tax), $this->currency ?: 'EUR', 'de_DE'),
                });
            }

            // discount_total
            if (getters.discountTotal) {
                // summary.push({
                //   key: 'discount-total',
                //   label: 'Rabatt',
                //   amount: getters.discountTotal
                //   // formatted: `-${getters.formattedDiscountTotal}`
                // });

                let key = 'discount-total';
                let label = 'Rabatt';
                // check if there is only one coupon and if it is a percentage one
                if (
                    state.checkout.form.coupons.length === 1 &&
                    state.checkout.form.coupons[0]['type'] === 'percentage'
                ) {
                    const percentage = state.checkout.form.coupons[0].percentage;
                    if (percentage) {
                        key = 'discount-percentage';
                        label = `${percentage}% Rabatt`;
                    }
                }

                summary.push({
                    key,
                    label,
                    amount: getters.discountTotal
                    // formatted: `-${getters.formattedDiscountTotal}`,
                });
            }

            // shipping_fee
            if (getters.shippingFee && getters.shippingFee !== getters.shippingFeeNet) {
                summary.push({
                    key: 'shipping-fee',
                    label: 'Versandkosten',
                    amount: getters.shippingFee
                    // formatted: getters.formattedShippingFee
                });
            }
        } else {
            // everything is gross

            // sub_total
            if (getters.subTotal !== getters.grandTotal) {
                summary.push({
                    key: 'sub-total',
                    label: 'Zwischensumme',
                    amount: getters.subTotal
                    // formatted: getters.formattedSubTotal
                });
            }

            // discountTotal
            if (getters.discountTotal) {
                let key = 'discount-total';
                let label = 'Rabatt';
                // check if there is only one coupon and if it is a percentage one
                if (
                    state.checkout.form.coupons.length === 1 &&
                    state.checkout.form.coupons[0]['type'] === 'percentage'
                ) {
                    const percentage = state.checkout.form.coupons[0].percentage;
                    if (percentage) {
                        key = 'discount-percentage';
                        label = `${percentage}% Rabatt`;
                    }
                }

                summary.push({
                    key,
                    label,
                    amount: getters.discountTotal
                    // formatted: `-${getters.formattedDiscountTotal}`,
                });
            }

            // shipping_fee
            if (getters.shippingFee) {
                summary.push({
                    key: 'shipping-fee',
                    label: 'Versandkosten',
                    amount: getters.shippingFee
                    // formatted: getters.formattedShippingFee,
                });
            }
        }

        return summary;
    },
    customer: state => state.customer,
    checkout: state => state.checkout,
    addressOptions: (state, getters, rootState) => {
        let addressOptions = {
            billing: [],
            shipping: []
        };

        // find default addresses
        let billingAddress, shippingAddress;
        const addresses = get(rootState.auth, 'member.addresses') || [];

        addresses.forEach(address => {
            if (address.default_billing && !billingAddress) billingAddress = address;
            if (address.default_shipping && !shippingAddress) shippingAddress = address;
        });

        if (!billingAddress || !shippingAddress) {
            addresses.forEach(address => {
                if (address.type === 'billing' && !billingAddress) billingAddress = address;
                if (address.type === 'shipping' && !shippingAddress) shippingAddress = address;
            });
        }

        if (!billingAddress) billingAddress = addresses.find(address => address.type === 'main');
        if (!shippingAddress) shippingAddress = addresses.find(address => address.type === 'main');

        if (billingAddress) addressOptions.billing.push(billingAddress);
        if (shippingAddress) addressOptions.shipping.push(shippingAddress);

        const additionalBillingAddresses = addresses.find(address => {
            return (
                ['main', 'billing', 'additional'].includes(address.type) &&
                address.id !== get(shippingAddress, 'id') &&
                address.id !== get(billingAddress, 'id')
            );
        });
        if (additionalBillingAddresses)
            addressOptions.billing = [...addressOptions.billing, additionalBillingAddresses];

        const additionalShippingAddresses = addresses.find(address => {
            return (
                ['main', 'shipping', 'additional'].includes(address.type) &&
                address.id !== get(shippingAddress, 'id') &&
                address.id !== get(billingAddress, 'id')
            );
        });
        if (additionalShippingAddresses)
            addressOptions.shipping = [...addressOptions.shipping, additionalShippingAddresses];

        return addressOptions;
    },
    shippingMethodOptions: (state, getters, rootState, rootGetters) => {
        const shippingMethods = get(rootGetters, 'CMS.shop.shippingMethods') || [];

        const freeShipping = shippingMethods.find(el => el.service === 'free');
        const remainingShippingMethods = shippingMethods.filter(el => {
            if (freeShipping && freeShipping.id === el.id) return false;
            return el;
        });

        let options;
        if (freeShipping && remainingShippingMethods.length) {
            options = [freeShipping, ...remainingShippingMethods];
        } else if (remainingShippingMethods.length) {
            options = remainingShippingMethods;
        } else if (freeShipping) {
            options = [freeShipping];
        }

        return options;
    },
    subTotal: (state, getters) => {
        let sum = 0;

        if (state.customer && state.customer.tax_free) {
            sum = getters.subTotalNet;
        } else if (state.cart.articlePivots?.length) {
            state.cart.articlePivots.forEach(item => {
                const value = item.salePrice?.value || item.price.value;
                sum += value * item.qty;
            });
        }

        return round(sum, 2);
    },
    subTotalNet: state => {
        let sum = 0;

        if (state.cart.articlePivots?.length) {
            state.cart.articlePivots.forEach(item => {
                const value = item.salePrice?.net_value || item.price.net_value;
                sum += value * item.qty;
            });
        }

        return round(sum, 2);
    },
    discountTotal: (state, getters) => {
        let total = 0;

        if (state.checkout.form.coupons?.length) {
            // NOTE: percentage base
            let remainingSubTotal = getters.subTotal;
            // TODO: dont get subtotal from getSubTotal() method
            // instead calculate the relevant subTotal based on conditions
            state.checkout.form.coupons.forEach(coupon => {
                switch (coupon.scope) {
                    case 'product':
                        // TODO
                        break;

                    case 'article':
                        // TODO
                        break;

                    default:
                    case 'cart':
                    case 'purchase':
                        // NOTE: apply coupon either fixed or the percentag to the whole shopping cart
                        if (coupon.type === 'percentage') {
                            const percentage = parseFloat(coupon.percentage);
                            const amount = (remainingSubTotal / 100) * percentage;

                            remainingSubTotal -= amount;
                            total += amount;
                        } else if (coupon.type === 'fixed') {
                            total += coupon.amount;
                        }
                        break;
                }
            });
        }

        return Math.min(round(total, 2), getters.subTotal + getters.shippingFee);
    },
    shippingFee: (state, getters) => {
        let fee = 0;

        if (state.customer && state.customer.tax_free) {
            fee = getters.shippingFeeNet;
        } else {
            const shippingMethod = get(getters.checkout.form, 'shipping');
            if (shippingMethod && shippingMethod.id && state.cart.qty > 0) {
                fee = getShippingFee(shippingMethod);
            }
        }

        return round(fee, 2);
    },
    shippingFeeNet: (state, getters) => {
        let fee = 0;

        const shippingMethod = get(getters.checkout.form, 'shipping');
        if (shippingMethod && shippingMethod.id && state.cart.qty > 0) {
            fee = getShippingFee(shippingMethod, true);
        }
        return fee;
    },
    itemTaxTotal: state => {
        let sum = 0;

        if (!state.customer || (state.customer && !state.customer.tax_free && state.cart.articlePivots?.length)) {
            state.cart.articlePivots.forEach(item => {
                const price = item.salePrice || item.price;
                sum += (price.value - price.net_value) * item.qty;
            });
        }

        return round(sum, 2);
    },
    taxTotal: (state, getters) => {
        let taxTotal = 0;
        let grandTotal = 0;

        if (!state.customer || (state.customer && !state.customer.tax_free)) {
            taxTotal = getters.itemTaxTotal;
            grandTotal = getters.subTotal;

            if (getters.shippingFee && getters.shippingFeeNet) {
                const shippingFeeTaxAmount = getters.shippingFee - getters.shippingFeeNet;
                if (shippingFeeTaxAmount) taxTotal += shippingFeeTaxAmount;
                grandTotal += getters.shippingFee;
            }

            if (taxTotal > 0) {
                const factor = taxTotal / grandTotal;
                // console.log(grandTotal, taxTotal, factor);
                taxTotal = getters.grandTotal * factor;
            }
        }

        return round(taxTotal, 2);
    },
    grandTotal: (state, getters) => {
        let sum = 0;
        if (state.cart.articlePivots?.length) {
            sum = getters.subTotal + getters.shippingFee - getters.discountTotal;
        }
        return sum;
    },
    defaultCustomerGroup: (state, getters, rootState, rootGetters) => {
        return get(rootGetters, 'CMS.shop.defaultCustomerGroup');
    },
    defaultTax: (state, getters, rootState, rootGetters) => {
        return get(rootGetters, 'CMS.shop.defaultTax');
    },
    preferredListOrientation: state => state.preferredListOrientation
};

// mutations
const mutations = {
    setPreferredListOrientation(state, value) {
        if (['horizontal', 'vertical', 'justified'].includes(value)) {
            state.preferredListOrientation = value;
            localStorage.setItem('product-list-orientation', value);
        }
    },
    setCustomer(state, customer) {
        Vue.set(state, 'customer', Object.freeze(customer));
    },
    setCart(state, cart) {
        // check availability of all articlePivots
        // if stock !== null (manageStock)
        // update `available` and `qty_at_limit`

        if (cart.articlePivots && cart.articlePivots.length) {
            let qtyMap = {};
            cart.articlePivots.forEach(articlePivot => {
                if (!qtyMap[articlePivot.article.id]) {
                    qtyMap[articlePivot.article.id] = {
                        stock: articlePivot.stock,
                        cart: 0
                    };
                }

                qtyMap[articlePivot.article.id].cart += articlePivot.qty;
            });

            cart.articlePivots.forEach(articlePivot => {
                if (articlePivot.stock !== null) {
                    articlePivot.available =
                        qtyMap[articlePivot.article.id].stock >= qtyMap[articlePivot.article.id].cart;
                    articlePivot.qty_at_limit =
                        qtyMap[articlePivot.article.id].stock === qtyMap[articlePivot.article.id].cart;
                }
            });
        }
        Vue.set(state, 'cart', cart);
    },
    setCartInitialising(state, status) {
        Vue.set(state.cart, 'initialising', status);
    },
    setCartInitialised(state, status) {
        Vue.set(state.cart, 'initialised', status);
        if (status) Vue.set(state.cart, 'initialising', false);
    },
    setCheckoutInitialised(state, status) {
        Vue.set(state.checkout, 'initialised', status);
    },
    setCheckoutSaving(state, value) {
        Vue.set(state.checkout, 'saving', value);
    },
    setCheckoutSuccess(state, value) {
        Vue.set(state.checkout, 'success', value);
    },
    setCheckoutPosition(state, value) {
        Vue.set(state.checkout, 'position', value);
    },
    setCheckoutFormAccount(state, account) {
        Vue.set(state.checkout.form, 'account', account);
    },
    setCheckoutFormAccountField(state, { field, value }) {
        Vue.set(state.checkout.form.account, field, value);
    },
    setCheckoutFormBillingAddress(state, address) {
        const billingAddress = {
            id: address.id,
            gender: null,
            first_name: null,
            last_name: null,
            company: null,
            street: address.street,
            street_addition: address.street_addition,
            zipcode: address.zipcode,
            city: address.city,
            country: address.country,
            type: 'billing',
            latitude: address.latitude,
            longitude: address.longitude
        };

        Vue.set(state.checkout.form.account, 'billing_address', billingAddress);
    },
    setCheckoutFormBillingAddressField(state, { field, value }) {
        Vue.set(state.checkout.form.account.billing_address, field, value);
    },
    setCheckoutFormShippingAddress(state, shippingAddress) {
        Vue.set(state.checkout.form.account, 'shipping_address', shippingAddress);
    },
    setCheckoutFormShippingAddressField(state, { field, value }) {
        Vue.set(state.checkout.form.account.shipping_address, field, value);
    },
    setCheckoutFormShipping(state, value) {
        Vue.set(state.checkout.form, 'shipping', value);
    },
    setCheckoutFormPayment(state, value) {
        Vue.set(state.checkout.form, 'payment', value);
    },
    addCouponToCheckoutForm(state, coupon) {
        let coupons = cloneDeep(state.checkout.form.coupons);
        coupons = orderBy(coupons, ['position', 'id']);

        const i = coupons?.length || 0;
        // check if the requested coupon is already in state
        if (i > 0 && coupons.find(el => el.id === coupon.id)) return;
        // if (i && coupons[i - 1].discard_the_following_coupons) return;

        Vue.set(coupons, i, coupon);

        let skip = false;
        coupons = orderBy(coupons, ['position', 'id']).filter(el => {
            if (!skip) {
                if (el.discard_the_following_coupons) skip = true;
                console.log('>>>', skip);
                return el;
            }
        });
        Vue.set(state.checkout.form, 'coupons', coupons);
    },
    removeCouponFromCheckoutForm(state, coupon) {
        const coupons = state.checkout.form.coupons || [];
        const i = coupons.findIndex(el => el.id === coupon.id);
        if (i >= 0) Vue.delete(state.checkout.form.coupons, i);
    },
    clearCouponsCheckoutForm(state) {
        Vue.set(state.checkout.form, 'coupons', []);
    },
    setFocusedMedia(state, { product, media }) {
        if (media && media.type) {
            media = cloneDeep(media);
            product = cloneDeep(product);
            delete product.media;

            media.product = Object.freeze(product);
            Vue.set(state, 'focusedMedia', Object.freeze(media));
        } else {
            Vue.delete(state, 'focusedMedia');
        }
    },
    removeFocusedMedia(state) {
        state.focusedMedia = null;
    }
};

// actions
const actions = {
    fetchProductsPaginated(context, payload) {
        return ShopApi.fetchProductsPaginated(payload);
    },
    fetchProductsWithArticlesPaginated(context, payload) {
        return ShopApi.fetchProductsPaginated(payload, true);
    },
    fetchProduct(context, payload) {
        return ShopApi.fetchProduct(payload);
    },
    saveCart({ state, rootGetters }) {
        return new Promise((resolve, reject) => {
            ShopApi.saveShoppingCartLocal(state.cart.articlePivots);

            if (rootGetters['auth/isAuthenticated']) {
                const articlePivots = removeKey(cloneDeep(state.cart.articlePivots), '__typename');
                ShopApi.debouncedSaveShoppingCart(articlePivots)
                    .then(resolve)
                    .catch(reject);
            } else {
                resolve();
            }
        });
    },
    addArticleToCart({ commit, dispatch, state, getters }, payload) {
        const article = payload.article;
        if (!article) throw new Error('missing article');

        let qty = payload.qty || 1;

        let propertyValues = [];
        let productPropertyValues = [];

        if (payload.propertyValues) propertyValues = payload.propertyValues;
        else if (payload.productPropertyValues) productPropertyValues = payload.productPropertyValues;

        if (!propertyValues.length) {
            article.propertyPivots.forEach(pivot => propertyValues.push({ id: pivot.value.id }));
            if (productPropertyValues.length) productPropertyValues.forEach(id => propertyValues.push({ id }));

            if (!propertyValues.length && (article.propertyPivots.length || productPropertyValues.length)) {
                throw new Error('missing property values');
            }
        }

        // handle selectable product properties
        let articlePivots = cloneDeep(state.cart.articlePivots) || [];
        const index = articlePivots.findIndex(el => {
            const diff = el.propertyValues.filter(value => !propertyValues.some(v => v.id === value.id));
            return el.article.id === article.id && !diff.length;
        });

        // TODO
        // check if manageStock
        // and if qty fits

        if (index >= 0) {
            articlePivots[index].qty += qty;
        } else {
            commit('setCartInitialised', false);
            articlePivots.push({
                article: { id: article.id },
                price: cloneDeep(article.price),
                salePrice: cloneDeep(article.salePrice),
                // available: true,
                // qty_at_limit: false,
                // valid: true,
                propertyValues,
                qty
            });
        }

        // const defaultCustomerGroup = get(getters, 'defaultCustomerGroup');
        const defaultTax = get(getters, 'defaultTax');
        // const customer = get(state, 'customer');

        const price = article.salePrice || article.price;
        // findPrice(article, customer, defaultCustomerGroup, true) ||
        // findPrice(article.product, customer, defaultCustomerGroup, true);

        const taxRate = get(price, 'tax.value') || defaultTax.value;
        const tax = round((price.value * taxRate) / (100 + taxRate), 2);

        const cart = {
            initialised: state.cart.initialised,
            initialising: state.cart.initialising,
            qty: state.cart.qty + qty,
            sum: state.cart.sum + price.value * qty,
            tax: state.cart.tax + tax,
            articlePivots
        };

        commit('setCart', cart);
        dispatch('saveCart');
    },
    removeArticleFromCart({ commit, dispatch, state, getters }, payload) {
        const article = payload.article;
        if (!article) throw new Error('missing article');

        let qty = payload.qty || 1;

        let propertyValues = [];
        let productPropertyValues = [];

        if (payload.propertyValues) propertyValues = payload.propertyValues;
        else if (payload.productPropertyValues) productPropertyValues = payload.productPropertyValues;

        if (!propertyValues.length) {
            article.propertyPivots.forEach(pivot => propertyValues.push({ id: pivot.value.id }));
            if (productPropertyValues.length) productPropertyValues.forEach(id => propertyValues.push({ id }));

            if (!propertyValues.length && (article.propertyPivots.length || productPropertyValues.length)) {
                throw new Error('missing property values');
            }
        }

        // handle selectable product properties
        let articlePivots = cloneDeep(state.cart.articlePivots) || [];
        const index = articlePivots.findIndex(el => {
            const diff = el.propertyValues.filter(value => !propertyValues.some(v => v.id === value.id));
            return el.article.id === article.id && !diff.length;
        });

        if (index >= 0) {
            qty = Math.min(qty, articlePivots[index].qty);
            articlePivots[index].qty -= qty;

            if (articlePivots[index].qty <= 0) articlePivots.splice(index, 1);

            // const defaultCustomerGroup = get(getters, 'defaultCustomerGroup');
            const defaultTax = get(getters, 'defaultTax');
            // const customer = get(state, 'customer');

            const price = article.salePrice || article.price;
            // findPrice(article, customer, defaultCustomerGroup, true) ||
            // findPrice(article.product, customer, defaultCustomerGroup, true);

            const taxRate = get(price, 'tax.value') || defaultTax.value;
            const tax = round((price.value * taxRate) / (100 + taxRate), 2);

            const cart = {
                initialised: state.cart.initialised,
                initialising: state.cart.initialising,
                qty: state.cart.qty - qty,
                sum: state.cart.sum - price.value,
                tax: state.cart.tax - tax,
                articlePivots
            };

            commit('setCart', cart);
            return dispatch('saveCart');
        }
    },
    updateArticleQtyInCart({ commit, dispatch, state, getters }, payload) {
        const article = payload.article;
        if (!article) throw new Error('missing article');

        let qty = payload.qty;

        let propertyValues = [];
        let productPropertyValues = [];

        if (payload.propertyValues) propertyValues = payload.propertyValues;
        else if (payload.productPropertyValues) productPropertyValues = payload.productPropertyValues;

        if (!propertyValues.length) {
            article.propertyPivots.forEach(pivot => propertyValues.push({ id: pivot.value.id }));
            if (productPropertyValues.length) productPropertyValues.forEach(id => propertyValues.push({ id }));

            if (!propertyValues.length) throw new Error('missing property values');
        }

        // handle selectable product properties
        let articlePivots = cloneDeep(state.cart.articlePivots) || [];
        const index = articlePivots.findIndex(el => {
            const diff = el.propertyValues.filter(value => !propertyValues.some(v => v.id === value.id));
            return el.article.id === article.id && !diff.length;
        });

        qty = parseInt(qty);
        if (isNaN(qty)) qty = 0;
        else if (qty > 1000) qty = 1000;

        let q = qty;
        if (index >= 0) {
            q = qty - articlePivots[index].qty;
            articlePivots[index].qty = qty;

            if (articlePivots[index].qty <= 0) articlePivots.splice(index, 1);
        } else if (qty > 0) {
            articlePivots.push({
                article: { id: article.id },
                propertyValues,
                qty
            });
        }

        // const defaultCustomerGroup = get(getters, 'defaultCustomerGroup');
        const defaultTax = get(getters, 'defaultTax');
        // const customer = get(state, 'customer');

        const price = article.salePrice || article.price;
        // findPrice(article, customer, defaultCustomerGroup, true) ||
        // findPrice(article.product, customer, defaultCustomerGroup, true);

        const taxRate = get(price, 'tax.value') || defaultTax.value;
        const tax = round((price.value * taxRate) / (100 + taxRate), 2);

        const cart = {
            initialised: state.cart.initialised,
            initialising: state.cart.initialising,
            qty: state.cart.qty + q,
            sum: state.cart.sum + price.value * q,
            tax: state.cart.tax + tax * q,
            articlePivots
        };

        commit('setCart', cart);
        dispatch('saveCart');
    },
    initCart(context, member = null) {
        const { commit, rootGetters, state } = context;

        if (!state.cart.initialised) {
            const customer = get(member, 'customer');
            if (customer) commit('setCustomer', customer);

            const articlePivots = ShopApi.getShoppingCartLocal();
            const data = get(customer, 'shoppingCart');

            // if member is logged in
            // get data from customer
            if (data && data.articlePivots && data.articlePivots.length) {
                const cart = {
                    initialised: true,
                    initialising: false,
                    sum: get(data, 'sum') || 0,
                    qty: get(data, 'qty') || 0,
                    tax: get(data, 'tax') || 0,
                    articlePivots: removeKey(cloneDeep(data.articlePivots), '__typename')
                };
                commit('setCart', cart);
                ShopApi.saveShoppingCartLocal(cart.articlePivots);
            }

            // otherwise initialise shopping cart by local values
            else if (articlePivots && articlePivots.length) {
                ShopApi.fetchShoppingCart(articlePivots).then(data => {
                    const cart = {
                        initialised: true,
                        initialising: false,
                        sum: get(data, 'sum') || 0,
                        qty: get(data, 'qty') || 0,
                        tax: get(data, 'tax') || 0,
                        articlePivots: removeKey(cloneDeep(data.articlePivots), '__typename')
                    };
                    commit('setCart', cart);

                    // if is authenticated
                    // and server returned no cart for customer
                    // but there are articles in the guest cart
                    // add the guest cart articles to the customer cart
                    // save the customer cart
                    // and clear the guest cart
                    if (rootGetters['auth/isAuthenticated']) {
                        ShopApi.saveShoppingCart(cart.articlePivots);
                        // ShopApi.saveShoppingCartLocal([], 'guest');
                    }

                    ShopApi.saveShoppingCartLocal(cart.articlePivots);
                });
            } else {
                // empty cart
                const cart = {
                    initialised: true,
                    initialising: false,
                    sum: 0,
                    qty: 0,
                    tax: 0,
                    articlePivots: []
                };
                commit('setCart', cart);
            }

            if (customer?.coupons?.length && !state.checkout.form.coupons?.length) {
                orderBy(customer.coupons, ['position', 'id']).forEach(coupon => {
                    if (coupon.code_required === false && coupon.applicable) {
                        commit('addCouponToCheckoutForm', coupon);
                    }
                });
            }

            commit('setCartInitialised', true);
        }
    },
    clearCart({ commit, dispatch }) {
        commit('setCart', {
            initialised: state.cart.initialised,
            initialising: state.cart.initialising,
            articlePivots: [],
            qty: 0,
            sum: 0,
            tax: 0
        });
        commit('clearCouponsCheckoutForm');
        dispatch('saveCart');
    },
    initCheckoutForm({ state, getters, rootGetters, commit }) {
        let account = cloneDeep(state.checkout.form.account);
        const member = rootGetters['auth/member'];

        account.id = undefined;
        account.billing_address.id = undefined;
        account.shipping_address.id = undefined;

        // init form
        if (rootGetters['auth/isAuthenticated'] && member && member.id) {
            account.id = member.id;
            account.gender = member.gender;
            account.first_name = member.first_name;
            account.last_name = member.last_name;
            account.phone = member.phone;
            account.email = member.email;

            // get default addresses from state

            if (getters.addressOptions.billing.length) {
                const billingAddress = getters.addressOptions.billing[0];
                account.billing_address = {
                    id: billingAddress.id,
                    gender: billingAddress.gender,
                    first_name: billingAddress.first_name,
                    last_name: billingAddress.last_name,
                    company: billingAddress.company,
                    street: billingAddress.street,
                    street_addition: billingAddress.street_addition,
                    zipcode: billingAddress.zipcode,
                    city: billingAddress.city,
                    country: billingAddress.country,
                    type: 'billing',
                    latitude: billingAddress.latitude,
                    longitude: billingAddress.longitude
                };
            }

            if (getters.addressOptions.shipping.length) {
                const shippingAddress = getters.addressOptions.shipping[0];
                account.shipping_address = {
                    id: shippingAddress.id,
                    gender: shippingAddress.gender,
                    first_name: shippingAddress.first_name,
                    last_name: shippingAddress.last_name,
                    company: shippingAddress.company,
                    street: shippingAddress.street,
                    street_addition: shippingAddress.street_addition,
                    zipcode: shippingAddress.zipcode,
                    city: shippingAddress.city,
                    country: shippingAddress.country,
                    type: 'shipping',
                    latitude: shippingAddress.latitude,
                    longitude: shippingAddress.longitude
                };
            }
        } else {
            // account.billing_address.id = uniqid('new:')
            // account.shipping_address.id = uniqid('new:')
        }

        commit('setCheckoutFormAccount', account);

        // set default shipping method

        let shipping = cloneDeep(state.checkout.form.shipping);
        if (!shipping.id && getters?.shippingMethodOptions?.length) {
            commit('setCheckoutFormShipping', getters.shippingMethodOptions[0]);
        }
    },
    submitCheckoutForm({ state, commit, dispatch }, paymentGatewayResponseId = null) {
        if (state.checkout.wait) {
            const errorMessage = {
                id: uniqid(),
                // title: 'Lucky Look',
                // text: 'Schneller als der eigene Schatten',
                title: 'Sie sind zu schnell',
                text: 'Versuchen Sie es nochmal.',
                type: 'warning',
                autoclose: 4000
            };
            dispatch('message/add', errorMessage, { root: true });
        } else {
            if (state.cart.articlePivots && state.cart.articlePivots.length) {
                // this.saving = true;
                commit('setCheckoutSaving', true);

                let items = [];
                state.cart.articlePivots.forEach(pivot => {
                    const item = {
                        qty: pivot.qty,
                        article: {
                            id: pivot.article.id
                        },
                        propertyValues: pivot.propertyValues.map(pv => ({ id: pv.id }))
                    };
                    items.push(item);
                });

                let coupons = [];
                if (state.checkout.form.coupons?.length) {
                    state.checkout.form.coupons.forEach(el => coupons.push(pick(el, ['id', 'code'])));
                }

                const payload = {
                    purchaser: {
                        gender: get(state.checkout, 'form.account.gender'),
                        first_name: get(state.checkout, 'form.account.first_name'),
                        last_name: get(state.checkout, 'form.account.last_name'),
                        email: get(state.checkout, 'form.account.email'),
                        phone: get(state.checkout, 'form.account.phone'),
                        mobile: get(state.checkout, 'form.account.mobile'),
                        billingAddress: get(state.checkout, 'form.account.billing_address'),
                        shippingAddress: get(state.checkout, 'form.account.shipping_address'),

                        create: get(state.checkout, 'form.account.create', false),
                        password: get(state.checkout, 'form.account.password')
                    },
                    items,
                    coupons,
                    paymentMethod: {
                        id: state.checkout.form.payment.id
                    },
                    shippingMethod: {
                        id: state.checkout.form.shipping.id
                    }
                };

                if (paymentGatewayResponseId) payload.payment_gateway_response_id = paymentGatewayResponseId;

                // NOTE: don't always clear the cart
                // paypal, stripe, ...
                return ShopApi.createPurchase(payload)
                    .then(data => {
                        if (data.errors === null && data.id && data.id > 0) {
                            switch (data.paymentMethod.service) {
                                case 'paypal':
                                case 'stripe':
                                    // TODO
                                    break;

                                default:
                                    dispatch('clearCart');
                                    commit('setCheckoutPosition', 1);
                                    dispatch(
                                        'message/add',
                                        {
                                            id: uniqid(),
                                            title: 'Erfolg',
                                            text: 'Ihre Bestellung wurde erfolgreich übermittelt.',
                                            type: 'success',
                                            autoclose: 4000
                                        },
                                        { root: true }
                                    );
                                    break;
                            }
                        } else {
                            // dispatch('message/add', {
                            //   id: uniqid(),
                            //   title: 'Fehler',
                            //   text: 'Es gibt ein technisches Problem.<br>Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                            //   type: 'error',
                            //   autoclose: 8000
                            // }, { root: true });
                        }

                        return data;
                    })
                    .finally(() => {
                        // this.saving = false
                        commit('setCheckoutSaving', false);
                    });

                // return dispatch('createPurchase', data)
                //   .then(() => {
                //     console.log(1)
                //     this.success = true;
                //     dispatch('message/add', {
                //       id: uniqid(),
                //       title: 'Erfolg',
                //       text: 'Ihre Bestellung wurde erfolgreich übermittelt.',
                //       type: 'success',
                //       autoclose: 4000
                //     }, { root: true });
                //   })
                //   .finally(() => {
                //     console.log(2)
                //     // this.saving = false
                //     commit('setCheckoutSaving', false)
                //   });
            }
        }
    },
    approvePayment({ dispatch, commit }, id) {
        commit('setCheckoutSaving', true);

        const purchase = { id };
        return ShopApi.approvePayment(purchase)
            .then(() => {
                dispatch(
                    'message/add',
                    {
                        id: uniqid(),
                        title: 'Erfolg',
                        text: 'Ihre Bestellung wurde erfolgreich übermittelt.',
                        type: 'success',
                        autoclose: 4000
                    },
                    { root: true }
                );
                dispatch('clearCart');
                commit('setCheckoutPosition', 1);
            })
            .catch(() => {
                dispatch(
                    'message/add',
                    {
                        id: uniqid(),
                        title: 'Fehler',
                        text:
                            'Es gibt ein technisches Problem.<br>Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                        type: 'error',
                        autoclose: 8000
                    },
                    { root: true }
                );
            })
            .finally(() => {
                commit('setCheckoutSaving', false);
            });
    },
    createPurchase({ dispatch }, payload) {
        return ShopApi.createPurchase(payload).then(() => dispatch('clearCart'));
    },
    applyCoupon({ state, commit, dispatch }, code) {
        return ShopApi.applyCoupon(code)
            .then(coupon => {
                if (coupon) {
                    if (
                        state.checkout.form.coupons.length &&
                        state.checkout.form.coupons.find(el => el.id === coupon.id)
                    ) {
                        dispatch(
                            'message/add',
                            {
                                id: uniqid(),
                                title: 'Achtung',
                                text: 'Sie haben diesen Gutscheincode bereits angewendet',
                                type: 'warning',
                                autoclose: 8000
                            },
                            { root: true }
                        );
                    } else {
                        coupon = cloneDeep(coupon);
                        coupon.code = code;
                        commit('addCouponToCheckoutForm', coupon);

                        let text = 'Gutschein erfolgreich angewendet';
                        if (coupon.code === null) text = 'Rabatt erfolgreich angewendet';
                        dispatch(
                            'message/add',
                            {
                                id: uniqid(),
                                title: 'Erfolg',
                                text,
                                type: 'success',
                                autoclose: 4000
                            },
                            { root: true }
                        );
                    }
                } else {
                    dispatch(
                        'message/add',
                        {
                            id: uniqid(),
                            title: 'Fehler',
                            text: 'Leider konnten wir den Gutscheincode nicht zuordnen',
                            type: 'error',
                            autoclose: 8000
                        },
                        { root: true }
                    );
                }
            })
            .catch(() => {
                dispatch(
                    'message/add',
                    {
                        id: uniqid(),
                        title: 'Fehler',
                        text:
                            'Es gibt ein technisches Problem.<br>Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                        type: 'error',
                        autoclose: 8000
                    },
                    { root: true }
                );
            });
    }
};

export default {
    namespaced: true,

    state,
    getters,
    actions,
    mutations
};
